import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { pageFromParam, pageToParam, withoutPageParams } from '../utils/pageParam';

type PageUpdate = number | ((page: number) => number);

/**
 * A list's page number, kept in the address as `?<key>=N` (page 1 = no parameter),
 * so a reload or a shared link opens the same page. Changes replace the current
 * history entry: Back leaves the list instead of stepping through its pages.
 * A page with several lists gives each its own key.
 */
export function usePageParam(key = 'page'): [number, (update: PageUpdate) => void] {
  const [params, setParams] = useSearchParams();
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;
  const page = pageFromParam(params.get(key));
  const setPage = useCallback((update: PageUpdate) => {
    setParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      const value = pageToParam(typeof update === 'function' ? update(pageFromParam(prev.get(key))) : update);
      if (value) next.set(key, value); else next.delete(key);
      return next;
    }, { replace: true });
  }, [key]);
  return [page, setPage];
}

/**
 * Runs `effect` when one of `deps` changes, never on mount — so "back to page 1
 * when the filter changes" does not undo a `?page=` the page was opened with.
 * Compares values, so React's StrictMode double run does not count as a change.
 */
export function useOnChange(effect: () => void, deps: unknown[]): void {
  const prev = useRef(deps);
  const run = useRef(effect);
  run.current = effect;
  useEffect(() => {
    const changed = deps.length !== prev.current.length || deps.some((d, i) => !Object.is(d, prev.current[i]));
    prev.current = deps;
    if (changed) run.current();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Clears `<prefix>` and every `<prefix>-…` page parameter at once: for a page whose
 * lists come and go (a section per folder), so one search change sends them all
 * back to their first page.
 */
export function useResetPageParams(prefix = 'page'): () => void {
  const [, setParams] = useSearchParams();
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;
  return useCallback(() => {
    setParamsRef.current((prev) => withoutPageParams(prev, prefix), { replace: true });
  }, [prefix]);
}
